/**
 * NavbarEditorPanel — dedicated Header editor. The Header is no longer customizable beyond
 * its navigation links: no logo, no layout selection. Exposes exactly:
 *   - Visibility (show/hide the section — a structural toggle, not "header customization")
 *   - Navigation links (label + destination/anchor) — the only editable content
 * Color is controlled globally from Design (`branding.brandColor`), never here.
 *
 * Removed on purpose (see production `Navbar.tsx` / navbar.css for the runtime side of
 * this same simplification):
 *   - Logo / logo upload — the Header no longer renders a logo at all.
 *   - Layout selector — the Header layout is fixed to `standard`; the registry no longer
 *     lists `minimal`/`centered` as selectable (they remain valid `NavbarLayout` schema
 *     values for backward compatibility with old data — see registerBuiltins.ts — the
 *     renderer just no longer offers them as a live choice).
 *   - The CTA/Button fields — the reference button still renders if the schema already
 *     has one (unrelated to this simplification), but editing it is out of scope: the
 *     Header only allows editing navigation links now.
 */
import { Plus, Trash2 } from 'lucide-react'
import { useBuilderEditor } from './editorContext'
import { Fieldset, TextField, ToggleField } from './controls'

export function NavbarEditorPanel({ sectionId }: { sectionId: string }) {
  const e = useBuilderEditor()
  const section = e.schema.pages[0]?.sections.find((s) => s.id === sectionId)
  if (!section) {
    return <p className="px-5 py-6 text-xs text-gray-400">This section is no longer available.</p>
  }

  const links = section.content.items ?? []

  return (
    <div>
      <Fieldset title="Visibility">
        <ToggleField
          label="Show navbar"
          description="Hide to remove it from the published site"
          checked={section.visible}
          onChange={() => e.toggleSectionVisible(sectionId)}
        />
      </Fieldset>

      <Fieldset title="Navigation links">
        {links.map((it) => (
          <div key={it.id} className="flex flex-col gap-2 rounded-xl border border-gray-100 p-3">
            <TextField label="Label" value={it.title} onChange={(v) => e.setNavLink(sectionId, it.id, 'title', v)} />
            <TextField label="Link" value={it.value ?? ''} onChange={(v) => e.setNavLink(sectionId, it.id, 'value', v)} />
            <button
              type="button"
              onClick={() => e.removeItem(sectionId, it.id)}
              className="flex items-center gap-1 self-start rounded-lg px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:text-red-600"
            >
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => e.addItem(sectionId)}
          className="flex items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50"
        >
          <Plus className="h-3.5 w-3.5" /> Add link
        </button>
      </Fieldset>
    </div>
  )
}
